import api from './api';

export interface FeeTypeItem {
  id: number;
  branch_id: string | null;
  name: string;
  code: string;
  description: string;
  category: string;
  is_recurring: boolean;
  is_required: boolean;
  default_amount: number;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type FeeTypeCategory =
  | 'tuition'
  | 'registration'
  | 'books'
  | 'uniform'
  | 'activities'
  | 'exam'
  | 'transportation'
  | 'meals'
  | 'facilities'
  | 'other';

export interface CreateFeeTypePayload {
  name: string;
  code: string;
  description?: string;
  category: FeeTypeCategory;
  is_recurring: boolean;
  is_required: boolean;
  default_amount: number;
  currency: string;
}

export interface UpdateFeeTypePayload {
  name: string;
  description?: string;
  is_recurring: boolean;
  is_required: boolean;
  default_amount: number;
  is_active: boolean;
}

export interface CreateStudentFeePayload {
  student_id: number;
  fee_type_id: number;
  fee_schedule_id?: number;
  amount: number;
  currency: string;
  discount_amount?: number;
  discount_type?: 'fixed' | 'percent';
  discount_percentage?: number;
  academic_year_id?: number;
  class_id?: number;
  notes?: string;
  effective_date: string; // ISO string
}

export interface CreateFeePaymentPayload {
  student_fee_id: number;
  amount: number;
  currency: string;
  payment_method: string;
  payment_date: string; // ISO string
  due_date: string;     // ISO string
  notes?: string;
  payment_reference?: string;
}

export const feeManagementApi = {
  async getFeeTypes(activeOnly = true): Promise<FeeTypeItem[]> {
    const response = await api.get('/fee-management/fee-types', {
      params: { active_only: activeOnly ? 'true' : 'false' },
    });
    return response.data.data?.fee_types ?? response.data.data ?? [];
  },

  async createFeeType(payload: CreateFeeTypePayload): Promise<FeeTypeItem> {
    const response = await api.post('/fee-management/fee-types', payload);
    return response.data.data;
  },

  async updateFeeType(id: number, payload: UpdateFeeTypePayload): Promise<FeeTypeItem> {
    const response = await api.put(`/fee-management/fee-types/${id}`, payload);
    return response.data.data;
  },

  async deleteFeeType(id: number): Promise<void> {
    await api.delete(`/fee-management/fee-types/${id}`);
  },

  async createStudentFee(payload: CreateStudentFeePayload): Promise<{ id: number }> {
    const response = await api.post('/fee-management/student-fees', payload);
    return response.data.data;
  },

  async createFeePayment(payload: CreateFeePaymentPayload): Promise<void> {
    await api.post('/fee-management/payments', payload);
  },
};

export default feeManagementApi;
